import React, { useEffect, useState, useEffect as ReactUseEffect } from 'react';
import ReactDOM from 'react-dom';
import './FighterSelectorView.css';
import NFTscale from "./NFTScale";
import { BASE_URL } from '../../../../config';
import { MountFunction } from './MountFunction';
import { UnmountFunction } from './UnmountFunction';
import { RemountFunction } from './AtomicUnmount';

const IMAGE_FALLBACK = '/images/starfighter.png';

// Helper for stats extraction from traits_stats object
function extractStat(traits_stats, keys, defaultValue = 'NA') {
    for (let key of keys) {
        if (traits_stats[key]) return traits_stats[key];
    }
    return {};
}

function getTotalItemFind(stats) {
    return Object.values(stats)
        .map(s => (s && typeof s.item_find === 'number' ? s.item_find : 0))
        .reduce((sum, val) => sum + val, 0);
}

function formatOreAffinity(raw) {
    if (!raw || typeof raw !== "string") return "NA";
    let base = raw.replace(/_ore$/i, "");
    return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase();
}

function formatPair(a, b) {
    if (a === undefined && b === undefined) return 'NA';
    return `${a !== undefined ? a : 'NA'}/${b !== undefined ? b : 'NA'}`;
}

const STAT_LABELS = [
    {
        label: "Up/Down Movement",
        value: (stats) => formatPair(
            stats.engine?.movement_up ?? stats.afterburner?.movement_up,
            stats.engine?.movement_down ?? stats.afterburner?.movement_down
        ),
    },
    {
        label: "Armor", value: (stats) =>
            (stats.canopy?.armor || stats.fuselage?.armor) || 'NA'
    },
    {
        label: "Forward/Back Movement",
        value: (stats) => formatPair(
            stats.engine?.movement_forward ?? stats.afterburner?.movement_forward,
            stats.engine?.movement_back ?? stats.afterburner?.movement_back
        ),
    },
    {
        label: "Cargo Capacity", value: (stats) =>
            (stats["star fighter"]?.cargo_capacity) || 'NA'
    },
    {
        label: "Damage Reduction",
        value: (stats) =>
            stats.fuselage && stats.fuselage.damage_reduction !== undefined
                ? stats.fuselage.damage_reduction
                : 'NA'
    },
    {
        label: "Ore Affinity",
        value: (stats) =>
            stats.galaxy?.ore_affinity
                ? formatOreAffinity(stats.galaxy.ore_affinity)
                : 'NA'
    },
    {
        label: "Laser Damage", value: (stats) =>
            (stats.laser?.damage) || 'NA'
    },
    {
        label: "Missile Damage", value: (stats) =>
            (stats.missile?.player_damage_missile) || 'NA'
    },
    {
        label: "Item Find",
        value: (stats) => {
            const total = getTotalItemFind(stats);
            return typeof total === 'number'
                ? total.toFixed(2)
                : '0.00';
        }
    }
];

// Accepts the NFT object, and an onClose function
const FighterSelectorView = ({ nft, userInfo, contractAddress, onClose, buttonMode = "mount", onMountChange }) => {
    const [imageUrl, setImageUrl] = useState(IMAGE_FALLBACK);
    const attributes = nft?.metadata?.attributes || [];
    const stats = nft?.traits_stats || {};
    const [imageError, setImageError] = useState(false);

    const [isConfirmingMount, setIsConfirmingMount] = useState(false);
    const [mountStatus, setMountStatus] = useState("");
    const [isConfirmingDismount, setIsConfirmingDismount] = useState(false);
    const [isConfirmingSwap, setIsConfirmingSwap] = useState(false);

    // NEW: hard UI lock while waiting for server confirm/fail
    const [isBlocking, setIsBlocking] = useState(false);

    // Prevent ESC from closing or other global key actions while blocking (defensive)
    useEffect(() => {
        const handler = (e) => {
            if (isBlocking && (e.key === 'Escape' || e.key === 'Esc')) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener('keydown', handler, true);
        return () => window.removeEventListener('keydown', handler, true);
    }, [isBlocking]);

    const confirmMountedFighter = async (action, maxTries = 10, delayMs = 5000) => {
        setMountStatus("Confirming with server...");
        let tries = 0;
        while (tries < maxTries) {
            try {
                const res = await fetch(`${BASE_URL}/confirm_mounted_fighter`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        address: userInfo.wallet_address,
                        jwt: userInfo.jwt,
                        nft_contract: contractAddress,
                        nft_id: nft.token_id,
                        action_taken: action
                    }),
                });
                const data = await res.json();
                if (data.valid) {
                    setMountStatus("Confirmed with server!");
                    return true;
                }
                // not valid yet; wait and retry
            } catch (e) {
                setMountStatus("Error contacting server, retrying...");
            }
            tries += 1;
            await new Promise(resolve => setTimeout(resolve, delayMs));
        }
        setMountStatus("Server did not confirm within 30 seconds.");
        return false;
    };

    useEffect(() => {
        let active = true;
        if (
            !userInfo?.wallet_address ||
            !userInfo?.jwt ||
            !contractAddress ||
            !nft?.token_id
        ) {
            setImageUrl(IMAGE_FALLBACK);
            setImageError(true);
            return;
        }
        setImageError(false);
        fetch(`${BASE_URL}/get_user_nft_image`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                address: userInfo.wallet_address,
                jwt: userInfo.jwt,
                nft_contract: contractAddress,
                nft_id: nft.token_id
            }),
        })
            .then(async (res) => {
                if (!active) return;
                if (res.ok) {
                    const blob = await res.blob();
                    setImageUrl(URL.createObjectURL(blob));
                } else {
                    setImageUrl(IMAGE_FALLBACK);
                    setImageError(true);
                }
            })
            .catch(() => {
                if (!active) return;
                setImageUrl(IMAGE_FALLBACK);
                setImageError(true);
            });
        return () => { active = false; };
    }, [nft, userInfo, contractAddress]);

    // Handlers for the mount flow
    const handleMountClick = () => setIsConfirmingMount(true);
    const handleCancelMount = () => {
        if (isBlocking) return; // don't allow cancel while blocking
        setIsConfirmingMount(false);
        setMountStatus("");
    };

    const handleConfirmMount = async () => {
        setMountStatus("");
        setIsBlocking(true);
        await MountFunction({
            walletAddress: userInfo.wallet_address,
            nftId: nft.token_id,
            collectionAddress: contractAddress,
            onStatus: setMountStatus,
            onSuccess: async () => {
                setMountStatus("Mounting complete! Waiting for confirmation...");
                const confirmed = await confirmMountedFighter("mount");
                if (confirmed) {
                    setMountStatus("Fighter mounted and confirmed!");
                    setTimeout(() => {
                        setIsConfirmingMount(false);
                        if (onMountChange) onMountChange();
                        onClose();
                        // no need to unset isBlocking; modal closes
                    }, 2000);
                } else {
                    // server never confirmed; unlock UI so user can try again/close
                    setIsBlocking(false);
                }
            },
            onError: (msg) => {
                setMountStatus("Error: " + msg);
                setIsBlocking(false);
            }
        });
    };

    // Dismount Handler
    const handleDismount = async () => {
        setMountStatus("");
        setIsBlocking(true);
        await UnmountFunction({
            walletAddress: userInfo.wallet_address,
            nftId: nft.token_id,
            collectionAddress: contractAddress,
            onStatus: setMountStatus,
            onSuccess: async () => {
                setMountStatus("Dismount complete! Waiting for confirmation...");
                const confirmed = await confirmMountedFighter("dismount");
                if (confirmed) {
                    setMountStatus("Fighter dismounted and confirmed!");
                    setTimeout(() => {
                        setIsConfirmingMount(false);
                        if (onMountChange) onMountChange();
                        onClose();
                    }, 5000);
                } else {
                    setIsBlocking(false);
                }
            },
            onError: (msg) => {
                setMountStatus("Error: " + msg);
                setIsBlocking(false);
            }
        });
    };

    // --- Atomic Swap Flow ---
    const handleSwapClick = () => setIsConfirmingSwap(true);
    const handleCancelSwap = () => {
        if (isBlocking) return;
        setIsConfirmingSwap(false);
    };
    const handleConfirmSwap = async () => {
        setMountStatus("");
        setIsBlocking(true);
        await RemountFunction({
            walletAddress: userInfo.wallet_address,
            newTokenId: nft.token_id,
            newCollectionAddress: contractAddress,
            onStatus: setMountStatus,
            onSuccess: async () => {
                setMountStatus("Swap complete! Waiting for confirmation...");
                const confirmed = await confirmMountedFighter("mount");
                if (confirmed) {
                    setMountStatus("Fighter swapped and confirmed!");
                    setTimeout(() => {
                        setIsConfirmingMount(false);
                        setIsConfirmingSwap(false);
                        if (onMountChange) onMountChange();
                        onClose();
                    }, 2000);
                } else {
                    setIsBlocking(false);
                }
            },
            onError: (msg) => {
                setMountStatus("Error: " + msg);
                setIsBlocking(false);
            }
        });
    };

    // --- Modal rendering ---
    return ReactDOM.createPortal(
        <div className="selectorview-overlay">
            <div className="selectorview-popup" aria-busy={isBlocking ? "true" : "false"}>
                <button
                    className="selectorview-close"
                    onClick={() => { if (!isBlocking) onClose(); }}
                    aria-disabled={isBlocking}
                    style={isBlocking ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                    title={isBlocking ? "Processing... please wait for confirmation" : "Close"}
                >
                    {'\u00D7'}
                </button>

                <div className="selectorview-main-row" style={isBlocking ? { filter: 'grayscale(0.2)', opacity: 0.85 } : undefined}>
                    <div className="selectorview-left-col">
                        <div className="selectorview-image-container">
                            <img
                                src={imageUrl}
                                alt={nft?.metadata?.name || 'NFT'}
                                className="selectorview-image"
                                style={imageError ? { opacity: 0.4 } : undefined}
                            />
                            {imageError && (
                                <div style={{
                                    color: "#ff5f5f",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    fontSize: "1.01em",
                                    marginTop: "6px",
                                    textShadow: "0 1px 3px #000, 0 0 12px #fff"
                                }}>
                                    Cannot load image.
                                </div>
                            )}
                        </div>
                        <NFTscale nft={{ ...nft, trait_stats: nft.traits_stats }} />
                    </div>

                    <div className="selectorview-traits-container">
                        {/* MOUNT */}
                        {isConfirmingMount ? (
                            <div style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '120px',
                                justifyContent: 'center', gap: 24
                            }}>
                                <div style={{
                                    color: '#b7d2ff', fontSize: '1.12em', textAlign: 'center', marginBottom: 10
                                }}>
                                    <strong>You are about to lock your Proxima Fighter NFT in the game contract.</strong>
                                    <br />
                                    This will enable your NFT to be played in game.<br />
                                    <br />
                                    <span style={{ color: "#ffb347" }}>Do you wish to proceed?</span>
                                </div>
                                <button
                                    className="selectorview-mount-btn"
                                    style={{ width: 220, fontSize: '1.2em', marginBottom: 10 }}
                                    onClick={handleConfirmMount}
                                    disabled={isBlocking || (mountStatus && mountStatus.includes("Mounting complete"))}
                                >
                                    Confirm Mount
                                </button>
                                <button
                                    className="selectorview-mount-btn"
                                    style={{
                                        width: 120,
                                        background: 'linear-gradient(90deg, #e84747 60%, #ffb3b3 100%)',
                                        border: '1.5px solid #e84747',
                                        color: '#fff',
                                        fontSize: '1.05em',
                                        fontWeight: 600,
                                        boxShadow: '0 0 8px #e84747b5',
                                        marginTop: 4,
                                        opacity: isBlocking ? 0.6 : 1
                                    }}
                                    onClick={handleCancelMount}
                                    disabled={isBlocking}
                                >
                                    Cancel
                                </button>
                                {mountStatus && !isBlocking && (
                                    <div style={{
                                        marginTop: 12, minHeight: 28,
                                        color: mountStatus.startsWith("Error") ? "#ff5f5f" : "#b7d2ff",
                                        fontWeight: 500, textAlign: "center"
                                    }}>
                                        {mountStatus}
                                    </div>
                                )}
                            </div>
                        ) : isConfirmingDismount ? (
                            // DISMOUNT
                            <div style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '120px',
                                justifyContent: 'center', gap: 24
                            }}>
                                <div style={{
                                    color: '#ffe175', fontSize: '1.12em', textAlign: 'center', marginBottom: 10
                                }}>
                                    <strong>You are about to dismount your Proxima Fighter NFT.</strong>
                                    <br />
                                    This will unlock your NFT from the game contract, making it unavailable in-game.<br />
                                    <br />
                                    <span style={{ color: "#db3333" }}>Do you wish to proceed?</span>
                                </div>
                                <button
                                    className="selectorview-mount-btn"
                                    style={{ width: 220, fontSize: '1.2em', background: "#db3333", marginBottom: 10 }}
                                    onClick={handleDismount}
                                    disabled={isBlocking || (mountStatus && mountStatus.includes("Dismount complete"))}
                                >
                                    Confirm Dismount
                                </button>
                                <button
                                    className="selectorview-mount-btn"
                                    style={{
                                        width: 120,
                                        background: 'linear-gradient(90deg, #e84747 60%, #ffb3b3 100%)',
                                        border: '1.5px solid #e84747',
                                        color: '#fff',
                                        fontSize: '1.05em',
                                        fontWeight: 600,
                                        boxShadow: '0 0 8px #e84747b5',
                                        marginTop: 4,
                                        opacity: isBlocking ? 0.6 : 1
                                    }}
                                    onClick={() => { if (!isBlocking) setIsConfirmingDismount(false); }}
                                    disabled={isBlocking}
                                >
                                    Cancel
                                </button>
                                {mountStatus && !isBlocking && (
                                    <div style={{
                                        marginTop: 12, minHeight: 28,
                                        color: mountStatus.startsWith("Error") ? "#ff5f5f" : "#ffe175",
                                        fontWeight: 500, textAlign: "center"
                                    }}>
                                        {mountStatus}
                                    </div>
                                )}
                            </div>
                        ) : isConfirmingSwap ? (
                            // SWAP
                            <div style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '120px',
                                justifyContent: 'center', gap: 24
                            }}>
                                <div style={{
                                    color: '#91baff', fontSize: '1.12em', textAlign: 'center', marginBottom: 10
                                }}>
                                    <strong>You are about to swap your mounted Proxima Fighter NFT.</strong>
                                    <br />
                                    This will dismount your current fighter and instantly mount this one.<br />
                                    <br />
                                    <span style={{ color: "#ffb347" }}>Proceed with swap?</span>
                                </div>
                                <button
                                    className="selectorview-mount-btn"
                                    style={{ width: 220, fontSize: '1.2em', background: "#4977d1", marginBottom: 10 }}
                                    onClick={handleConfirmSwap}
                                    disabled={isBlocking || (mountStatus && mountStatus.includes("Swap complete"))}
                                >
                                    Confirm Swap
                                </button>
                                <button
                                    className="selectorview-mount-btn"
                                    style={{
                                        width: 120,
                                        background: 'linear-gradient(90deg, #e84747 60%, #ffb3b3 100%)',
                                        border: '1.5px solid #e84747',
                                        color: '#fff',
                                        fontSize: '1.05em',
                                        fontWeight: 600,
                                        boxShadow: '0 0 8px #e84747b5',
                                        marginTop: 4,
                                        opacity: isBlocking ? 0.6 : 1
                                    }}
                                    onClick={handleCancelSwap}
                                    disabled={isBlocking}
                                >
                                    Cancel
                                </button>
                                {mountStatus && !isBlocking && (
                                    <div style={{
                                        marginTop: 12, minHeight: 28,
                                        color: mountStatus.startsWith("Error") ? "#ff5f5f" : "#91baff",
                                        fontWeight: 500, textAlign: "center"
                                    }}>
                                        {mountStatus}
                                    </div>
                                )}
                            </div>
                        ) : (
                            // DEFAULT BUTTONS (NO CONFIRM)
                            <>
                                {buttonMode === "mount" && (
                                    <button
                                        className="selectorview-mount-btn"
                                        onClick={handleMountClick}
                                        disabled={isBlocking}
                                        style={isBlocking ? { opacity: 0.6 } : undefined}
                                    >
                                        Mount NFT
                                    </button>
                                )}
                                {buttonMode === "dismount" && (
                                    <button
                                        className="selectorview-mount-btn"
                                        style={{ background: "#db3333", ...(isBlocking ? { opacity: 0.6 } : {}) }}
                                        onClick={() => { if (!isBlocking) setIsConfirmingDismount(true); }}
                                        disabled={isBlocking || (mountStatus && mountStatus.includes("Dismount complete"))}
                                    >
                                        Dismount NFT
                                    </button>
                                )}
                                {buttonMode === "swap" && (
                                    <button
                                        className="selectorview-mount-btn"
                                        onClick={handleSwapClick}
                                        disabled={isBlocking}
                                        style={isBlocking ? { opacity: 0.6 } : undefined}
                                    >
                                        Swap NFT
                                    </button>
                                )}
                                <div className="selectorview-traits-grid" style={isBlocking ? { pointerEvents: 'none', opacity: 0.85 } : undefined}>
                                    {attributes.map((attr, idx) => (
                                        <div className="selectorview-trait" key={idx}>
                                            <span className="selectorview-trait-name">{attr.trait_type}</span>
                                            <span className="selectorview-trait-value">{attr.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* --- Info Grid (unchanged) --- */}
                <div className="selectorview-info" style={isBlocking ? { pointerEvents: 'none', opacity: 0.85 } : undefined}>
                    <h4>NFT Information</h4>
                    <div className="selectorview-info-grid">
                        {STAT_LABELS.map(({ label, value }, idx) => (
                            <div key={idx}>
                                <strong>{label}:</strong> {value(stats)}
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '0.99em', color: '#a7c9f5' }}>
                        <strong>ID:</strong> {nft.token_id}<br />
                        <strong>Name:</strong> {nft?.metadata?.name}<br />
                        <strong>Description:</strong> {nft?.metadata?.description}
                    </div>
                </div>

                {/* BLOCKING SCRIM (captures all clicks + shows status) */}
                {isBlocking && (
                    <div
                        aria-live="polite"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(5, 12, 26, 0.55)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 20,
                            pointerEvents: 'auto',
                            backdropFilter: 'blur(1px)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 12,
                            padding: '16px 18px',
                            borderRadius: 10,
                            border: '1px solid #91baff',
                            background: 'linear-gradient(135deg, #1a2a4d 0%, #233451 100%)',
                            boxShadow: '0 0 18px #4977d188'
                        }}>
                            {/* simple spinner */}
                            <div className="selectorview-spinner" />
                            
                            <div style={{ color: '#cde3ff', fontFamily: "'Orbitron','Segoe UI',Arial,sans-serif", textAlign: 'center' }}>
                                {mountStatus || 'Processing transaction...'}
                                <div style={{ fontSize: '0.88em', color: '#91baff', marginTop: 4 }}>
                                    Please wait for server confirmation.
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* tiny keyframes without touching your CSS file */}
            <style>{`
            @keyframes selectorview-spin {
                from { transform: rotate(0deg); }
                to   { transform: rotate(360deg); }
            }
        `}</style>
        </div>,
        document.body
    );
};

export default FighterSelectorView;
